/**
 * home-auth.js — a signed-in host is signed in on the homepage too.
 *
 * The landing page deliberately ships no Firebase SDK: nearly everyone who
 * lands here is a prospect, and firebase-auth.js alone is 173 KB before any of
 * our code. That stays true. What this adds is the other case — a returning
 * host — for whom the page already knows enough to act, and did not:
 *
 *   auth-manager.js keeps a synchronous localStorage flag, qrsvp_authed, set on
 *   sign-in and cleared on sign-out. The inline script at the top of the page
 *   reads it before first paint to swap the hero CTA to "My Events". That was
 *   the whole of the homepage's idea of "signed in": a different button.
 *
 * So the support chat could not see who they were (no attach control, no
 * account notices, no user context for the assistant — 2026-09-16, a host on
 * the homepage was told she was not signed in while she was), and the nav
 * offered a generic "My Events" with no name on it.
 *
 * This file pays the SDK cost ONLY when the flag says it will be worth it:
 *
 *   1. Flag absent → return. Not one extra byte for a prospect.
 *   2. Flag present → load the same firebase-app + firebase-auth the dashboard
 *      uses, initialise, publish window.firebaseAuth, and tell anyone listening
 *      (the help widget binds on the 'qr-auth-ready' event, because it has
 *      already initialised by the time a deferred script runs).
 *   3. Auth resolves to a user → replace the nav's "My Events" link with a chip
 *      carrying their name and avatar, with My Events / Sign out beneath it.
 *   4. Auth resolves to nobody → the flag was stale (signed out on another
 *      device, session expired). Clear it and drop the qr-authed class, so the
 *      page stops promising an account that is not there.
 *
 * The flag is a hint about whether to bother loading the SDK, never a
 * security boundary: identity still comes from Firebase every time.
 */
(function () {
  'use strict';

  const FLAG = 'qrsvp_authed';
  // A page that signs people in itself (tickets.html: its own Firebase and an
  // inline sign-in) loads this with data-page-auth and calls
  // qrHomeAuth.renderChip(user, auth) when it knows who is there (task #962).
  // Booting a second copy of the SDK on that page is never wanted.
  const PAGE_AUTH = !!(document.currentScript && document.currentScript.hasAttribute('data-page-auth'));
  let flagged = false;
  try { flagged = localStorage.getItem(FLAG) === '1'; } catch (_) { /* storage blocked */ }

  // Clarity: a returning host on the homepage clicks "Dashboard", not the
  // pitch, and was a third to half of its clicks (2026-10-15). Tagged, an
  // export can be filtered to the prospects the page is for. signed_in is
  // settled by Firebase below; with no flag it is already known to be "no".
  const clarityTag = (key, value) => {
    try { if (typeof window.clarity === 'function') window.clarity('set', key, value); } catch (_) { /* analytics never breaks the page */ }
  };
  // known_host: a host has signed in on this browser before (qrsvp_known_host,
  // which survives sign-out). signed_in = no with known_host = yes is a host
  // who signed out or lapsed; known_host = no is a prospect or a host on a new
  // device.
  let known = flagged;
  try { known = known || localStorage.getItem('qrsvp_known_host') === '1'; } catch (_) { /* storage blocked */ }
  clarityTag('known_host', known ? 'yes' : 'no');
  if (!flagged && !PAGE_AUTH) { clarityTag('signed_in', 'no'); return; }

  // Same SDK build the app pages ship, so a host moving between pages does not
  // pay for two versions and auth persistence is read the same way.
  const SDK = [
    'https://www.gstatic.com/firebasejs/8.10.1/firebase-app.js',
    'https://www.gstatic.com/firebasejs/8.10.1/firebase-auth.js'
  ];
  // Public web config — identical to assets/js/firebase-init.js. These values
  // are shipped to every browser by design; access is governed by rules.
  const CONFIG = {
    apiKey: 'AIzaSyCh25E_Bx9CfNa7WAGrqXocBnvLhvSL9s8',
    authDomain: 'auth.quikrsvp.com',
    projectId: 'eventrsvp-bb229',
    storageBucket: 'eventrsvp-bb229.firebasestorage.app',
    messagingSenderId: '270467682236',
    appId: '1:270467682236:web:73c7a72c942765a0ba65b9'
  };

  const COPY = {
    en: { signOut: 'Sign out', account: 'Your account' },
    es: { signOut: 'Cerrar sesión', account: 'Tu cuenta' },
    fr: { signOut: 'Se déconnecter', account: 'Votre compte' },
    pt: { signOut: 'Terminar sessão', account: 'A sua conta' },
    de: { signOut: 'Abmelden', account: 'Ihr Konto' },
    he: { signOut: 'התנתקות', account: 'החשבון שלך' },
    ar: { signOut: 'تسجيل الخروج', account: 'حسابك' }
  };
  const lang = String(document.documentElement.lang || 'en').slice(0, 2).toLowerCase();
  const T = COPY[lang] || COPY.en;

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src;
      s.async = false;           // keep app → auth order
      s.onload = resolve;
      s.onerror = () => reject(new Error('failed to load ' + src));
      document.head.appendChild(s);
    });
  }

  function clearStaleHint() {
    try { localStorage.removeItem(FLAG); } catch (_) { /* ignore */ }
    document.documentElement.classList.remove('qr-authed');
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  function initialOf(user) {
    const name = (user.displayName || user.email || '?').trim();
    return name.charAt(0).toUpperCase();
  }

  function firstName(user) {
    if (user.displayName) return user.displayName.trim().split(/\s+/)[0];
    if (user.email) return user.email.split('@')[0];
    return '';
  }

  function injectStyles() {
    if (document.getElementById('qr-home-auth-styles')) return;
    const st = document.createElement('style');
    st.id = 'qr-home-auth-styles';
    st.textContent = `
      .qr-profile { position: relative; display: inline-flex; align-items: center; }
      .qr-profile-btn { display: inline-flex; align-items: center; gap: .5rem; background: none; border: 0;
        padding: .25rem .4rem; border-radius: 999px; cursor: pointer; font: inherit; color: inherit; line-height: 1; }
      .qr-profile-btn:hover, .qr-profile-btn[aria-expanded="true"] { background: rgba(0,0,0,.05); }
      .qr-profile-avatar { width: 30px; height: 30px; border-radius: 50%; object-fit: cover; flex-shrink: 0;
        background: var(--gold, #b8925a); color: #fff; display: inline-flex; align-items: center; justify-content: center;
        font-weight: 600; font-size: .85rem; }
      .qr-profile-name { max-width: 9rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 500; }
      .qr-profile-caret { width: 14px; height: 14px; opacity: .7; }
      .qr-profile-menu { position: absolute; top: calc(100% + 8px); inset-inline-end: 0; min-width: 12rem;
        max-width: min(22rem, calc(100vw - 32px));
        background: #fff; border: 1px solid rgba(0,0,0,.08); border-radius: 12px; box-shadow: 0 12px 32px rgba(0,0,0,.12);
        padding: .35rem; z-index: 60; display: none; }
      .qr-profile-menu[data-open="true"] { display: block; }
      .qr-profile-menu a, .qr-profile-menu button { display: block; width: 100%; text-align: start; background: none;
        border: 0; padding: .55rem .75rem; border-radius: 8px; font: inherit; color: inherit; cursor: pointer;
        text-decoration: none; }
      .qr-profile-menu a:hover, .qr-profile-menu button:hover { background: rgba(0,0,0,.05); }
      .qr-profile-menu .qr-profile-who { padding: .5rem .75rem .35rem; font-size: .8rem; opacity: .65;
        overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .qr-profile-menu hr { border: 0; border-top: 1px solid rgba(0,0,0,.08); margin: .3rem 0; }
      /* Below 1250px the nav hides its sign-in link and shows the hamburger, and
         the page tunes that header as logo · Create RSVP · hamburger. The chip
         must not re-enter that row (it landed mid-bar), so identity moves into
         the drawer instead — which is where mobile users go for "My Events". */
      @media (max-width: 1250px) { .qr-profile { display: none; } }
      .qr-drawer-profile { display: flex; align-items: center; gap: .75rem; padding: .25rem 0 1rem;
        border-bottom: 1px solid rgba(0,0,0,.08); }
      .qr-drawer-profile .qr-profile-avatar { width: 40px; height: 40px; font-size: 1rem; }
      .qr-drawer-profile-text { min-width: 0; }
      .qr-drawer-profile-name { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .qr-drawer-profile-email { font-size: .8rem; opacity: .65; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .qr-drawer-signout { margin-inline-start: auto; flex-shrink: 0; background: none; border: 1px solid rgba(0,0,0,.18);
        border-radius: 999px; padding: .65rem 1rem; font: inherit; font-size: .85rem; color: inherit; cursor: pointer;
        white-space: nowrap; }
      .qr-drawer-signout:hover { background: rgba(0,0,0,.05); }
    `;
    document.head.appendChild(st);
  }

  /**
   * Turn the nav's "My Events" link into a chip with the host's name and
   * avatar, keeping the link's own localized label and href for the menu.
   */
  function renderChip(user, auth) {
    const link = document.querySelector('.nav-signin');
    if (!link || link.dataset.qrChip === '1') return null;
    injectStyles();

    const myEventsHref = link.getAttribute('href') || '/my-projects';
    // The homepages label the link "Sign in" for a signed-out visitor and keep
    // the signed-in wording in data-authed-label; other pages use the span.
    const myEventsLabel = link.dataset.authedLabel || (link.querySelector('span') || {}).textContent || 'Dashboard';
    const name = firstName(user);
    const photo = user.photoURL;

    const wrap = document.createElement('div');
    wrap.className = 'qr-profile';
    wrap.dataset.qrChip = '1';
    wrap.innerHTML = `
      <button type="button" class="qr-profile-btn" aria-haspopup="menu" aria-expanded="false" aria-label="${escapeHtml(T.account)}">
        ${photo
          ? `<img class="qr-profile-avatar" src="${escapeHtml(photo)}" alt="" referrerpolicy="no-referrer">`
          : `<span class="qr-profile-avatar" aria-hidden="true">${escapeHtml(initialOf(user))}</span>`}
        ${name ? `<span class="qr-profile-name">${escapeHtml(name)}</span>` : ''}
        <svg class="qr-profile-caret" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>
      </button>
      <div class="qr-profile-menu" role="menu">
        <div class="qr-profile-who" title="${escapeHtml(user.email || '')}">${escapeHtml(user.email || user.displayName || '')}</div>
        <a role="menuitem" href="${escapeHtml(myEventsHref)}">${escapeHtml(myEventsLabel.trim())}</a>
        <hr>
        <button type="button" role="menuitem" class="qr-profile-signout">${escapeHtml(T.signOut)}</button>
      </div>`;

    // The dashboard itself is one click away on the header's primary button,
    // which reads "Dashboard" for a signed-in host (index.html, qr-cta-authed).
    // Before that, the menu was the only header route: Clarity showed 19 of
    // 21 menu opens picking it, in three days (2026-10-15).
    link.replaceWith(wrap);
    // The link's class carried the nav's own layout rules (hidden on small
    // screens, gold on hover); keep them on the replacement.
    wrap.classList.add('nav-signin');

    const btn = wrap.querySelector('.qr-profile-btn');
    const menu = wrap.querySelector('.qr-profile-menu');
    const setOpen = (open) => { menu.dataset.open = open ? 'true' : 'false'; btn.setAttribute('aria-expanded', open ? 'true' : 'false'); };
    btn.addEventListener('click', (e) => { e.stopPropagation(); setOpen(menu.dataset.open !== 'true'); });
    document.addEventListener('click', () => setOpen(false));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') setOpen(false); });

    wrap.querySelector('.qr-profile-signout').addEventListener('click', async () => {
      setOpen(false);
      try { await auth.signOut(); } catch (_) { /* fall through to the reload either way */ }
      clearStaleHint();
      location.reload();
    });

    // Below 1250px the chip is hidden (see the stylesheet) and the drawer is
    // where a host expects to find themselves: put who-they-are at the top of
    // it, with Sign out on the same row. It used to be a list item placed
    // after the drawer's "my-projects" link, found by href$="my-projects";
    // the redesigned homepages link /my-projects?signin=1, nothing matched,
    // and phones had no way to sign out (task #1122). The identity row is
    // always there, so the button no longer depends on any link's href.
    const drawerLinks = document.querySelector('#drawer .drawer-links');
    if (drawerLinks && !document.querySelector('.qr-drawer-profile')) {
      const who = document.createElement('div');
      who.className = 'qr-drawer-profile';
      who.innerHTML = `
        ${photo
          ? `<img class="qr-profile-avatar" src="${escapeHtml(photo)}" alt="" referrerpolicy="no-referrer">`
          : `<span class="qr-profile-avatar" aria-hidden="true">${escapeHtml(initialOf(user))}</span>`}
        <div class="qr-drawer-profile-text">
          ${user.displayName ? `<div class="qr-drawer-profile-name">${escapeHtml(user.displayName)}</div>` : ''}
          <div class="qr-drawer-profile-email">${escapeHtml(user.email || '')}</div>
        </div>
        <button type="button" class="qr-drawer-signout">${escapeHtml(T.signOut)}</button>`;
      who.querySelector('.qr-drawer-signout').addEventListener('click', async () => {
        try { await auth.signOut(); } catch (_) { /* fall through to the reload either way */ }
        clearStaleHint();
        location.reload();
      });
      drawerLinks.insertAdjacentElement('beforebegin', who);
    }
    return wrap;
  }

  async function boot() {
    try {
      for (const src of SDK) await loadScript(src);
      if (!window.firebase) throw new Error('firebase global missing after load');
      if (!firebase.apps || !firebase.apps.length) firebase.initializeApp(CONFIG);
      const auth = firebase.auth();
      window.firebaseAuth = auth;
      // Anyone that initialised before us (the help widget does) binds here.
      window.dispatchEvent(new CustomEvent('qr-auth-ready', { detail: auth }));

      auth.onAuthStateChanged((user) => {
        clarityTag('signed_in', user && !user.isAnonymous ? 'yes' : 'no');
        if (user && !user.isAnonymous) {
          document.documentElement.classList.add('qr-authed');
          renderChip(user, auth);
          // For a page that tailors itself to who is there (corporate.html's
          // hero, task #963).
          window.dispatchEvent(new CustomEvent('qr-signed-in', { detail: { user, auth } }));
        } else {
          // The hint outlived the session. Stop advertising an account.
          clearStaleHint();
        }
      });
    } catch (err) {
      // A failed SDK load must cost the visitor nothing: the page is exactly
      // the page they would have had without this file.
      try { console.warn('[home-auth] not signed in on this page:', err.message); } catch (_) { /* ignore */ }
    }
  }

  // Called by a data-page-auth page; otherwise used by tests only.
  window.qrHomeAuth = { renderChip, clearStaleHint };
  if (PAGE_AUTH) return;

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
